import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

// Add auth token and user info to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.role) {
          config.headers['x-user-role'] = user.role;
        }
        // Build full roles array (primary role + any additional roles)
        let allRoles = [];
        if (user.roles) {
          try {
            allRoles = Array.isArray(user.roles) ? user.roles : JSON.parse(user.roles);
          } catch (_) { allRoles = []; }
        }
        // Always include primary role
        if (user.role && !allRoles.includes(user.role)) allRoles.unshift(user.role);
        if (allRoles.length > 0) {
          config.headers['x-user-roles'] = JSON.stringify(allRoles);
        }
        if (user.id) config.headers['x-user-id'] = user.id;
        if (user.username) config.headers['x-user-name'] = user.username;
      } catch (e) {
        console.error('Error parsing user from localStorage:', e);
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const contentService = {
  getAll: (params) => api.get('/content', { params }),
  getById: (id) => api.get(`/content/${id}`),
  create: (data) => api.post('/content', data),
  update: (id, data) => api.put(`/content/${id}`, data),
  delete: (id) => api.delete(`/content/${id}`),
  getAuditLogs: (id) => api.get(`/content/${id}/audit-logs`),
  getAllAuditLogs: (params) => api.get('/content/audit-logs', { params }),
  uploadScript: (id, formData) => api.post(`/content/${id}/script`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteScript: (id) => api.delete(`/content/${id}/script`),
  getScriptUrl: (id) => api.get(`/content/${id}/script/url`),
};

export const dropdownService = {
  getAll: () => api.get('/dropdowns'),
  getByType: (type) => api.get(`/dropdowns/${type}`)
};

export const awardService = {
  getAll: (params) => api.get('/awards', { params }),
  getById: (id) => api.get(`/awards/${id}`),
  create: (data) => api.post('/awards', data),
  update: (id, data) => api.put(`/awards/${id}`, data),
  delete: (id) => api.delete(`/awards/${id}`),
  getStatistics: (params) => api.get('/awards/statistics', { params }),
  getEmployeeAwards: (employeeId) => api.get(`/awards/employee/${employeeId}`)
};

export const expenseService = {
  getAll: (params) => api.get('/expenses', { params }),
  getById: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  approve: (id, data) => api.put(`/expenses/${id}/approve`, data),
  getAnalytics: (params) => api.get('/expenses/analytics', { params }),
  bulkCreate: (data) => api.post('/expenses/bulk', data),
  getFinancialOverview: (params) => api.get('/expenses/financial-overview', { params })
};

export const inventoryService = {
  getAll: (params) => api.get('/inventory', { params }),
  getById: (id) => api.get(`/inventory/${id}`),
  create: (data) => api.post('/inventory', data),
  update: (id, data) => api.put(`/inventory/${id}`, data),
  delete: (id) => api.delete(`/inventory/${id}`),
  getStatistics: () => api.get('/inventory/statistics'),
  importFromExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/inventory/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export const onboardingService = {
  getAll: (params) => api.get('/onboarding', { params }),
  getById: (id) => api.get(`/onboarding/${id}`),
  getStats: () => api.get('/onboarding/stats'),
  getNextEmployeeId: () => api.get('/onboarding/next-employee-id'),
  create: (data) => api.post('/onboarding', data),
  update: (id, data) => api.put(`/onboarding/${id}`, data),
  updateStatus: (id, status) => api.patch(`/onboarding/${id}/status`, { status }),
  delete: (id) => api.delete(`/onboarding/${id}`),
  // Offer letter PDF (blob download / preview)
  getOfferLetterUrl: (id) => `${API_URL}/onboarding/${id}/offer-letter`,
  downloadOfferLetter: (id) => api.get(`/onboarding/${id}/offer-letter`, { responseType: 'blob' }),
  sendOffer: (id, email) => api.post(`/onboarding/${id}/send-offer`, email ? { email } : {}),
  convertToEmployee: (id, data) => api.post(`/onboarding/${id}/convert`, data || {}),
};

export const meetingService = {
  getAll: (params) => api.get('/meetings', { params }),
  getById: (id) => api.get(`/meetings/${id}`),
  getSummary: () => api.get('/meetings/summary'),
  getAttendeeOptions: () => api.get('/meetings/attendee-options'),
  create: (data) => api.post('/meetings', data),
  update: (id, data) => api.put(`/meetings/${id}`, data),
  cancel: (id, reason) => api.post(`/meetings/${id}/cancel`, reason ? { reason } : {}),
  delete: (id) => api.delete(`/meetings/${id}`),
  testGoogle: () => api.post('/meetings/google/test', {}),
};

export const notificationService = {
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (config) => api.put('/notifications/settings', { config }),
  getLogs: (params) => api.get('/notifications/logs', { params }),
  sendTest: (channel = 'both') => api.post('/notifications/test', { channel }),
  testEvent: (eventType, channel = 'whatsapp') => api.post('/notifications/test-event', { eventType, channel }),
  triggerReport: (type) => api.post(`/notifications/trigger/${type}`),
  addEvent: (event) => api.post('/notifications/events', event),
  deleteEvent: (type) => api.delete(`/notifications/events/${type}`),
};

export const propertyService = {
  getAll: (params) => api.get('/property', { params }),
  getById: (id) => api.get(`/property/${id}`),
  create: (data) => api.post('/property', data),
  update: (id, data) => api.put(`/property/${id}`, data),
  delete: (id) => api.delete(`/property/${id}`),
  getStatistics: () => api.get('/property/statistics'),
};

export const accessControlService = {
  getMyPermissions: () => api.get('/access-control/me'),
  getMatrix: () => api.get('/access-control/matrix'),
  updateMatrix: (changes) => api.put('/access-control/matrix', { changes }),
};

export default api;
